import React from 'react';

import { LoadingButtonWithTooltip } from '@qality/common-components/src/molecules/LoadingButtonWithTooltip';
import { TextSearch } from '@qality/common-components/src/molecules/TextSearch';
import IssueStatusPicker from '@qality/common-components/src/picker/IssueStatusPicker';
import IssueStatusProvider from '@qality/common-components/src/picker/IssueStatusPicker/IssueStatusProvider';
import { IssueStatusesContext } from '@qality/common-components/src/picker/IssueStatusPicker/IssueStatusProvider/IssueStatusesProvider';
import { TestTypeMultiSelect } from '@qality/common-components/src/picker/TestTypeMultiSelect';
import RecentAndMoreProjectSelect, {
  ALL_PROJECTS,
} from '@qality/common-components/src/select/RecentAndMoreProjectSelect';
import {
  FEATURE_FLAG,
  PREMIUM_FEATURE_TOOLTIP_MESSAGE,
} from '@qality/common-config';
import { StorageOfUniqueServices } from '@qality/common-service';

import { useTestTypesContext } from '../../TestTypesPage/TestTypesProvider/TestTypeProvider';
import { useTestCaseRepositoryIssuesContext } from '../TestCaseRepositoryIssuesProvider';
import {
  ContainerStyled,
  SelectWrapperStyled,
  TextSearchContainerStyled,
} from './TestCaseRepositoryFilters.styled';
import { useTestCaseRepositoryFiltersContext } from './TestCaseRepositoryFiltersProvider';

export default function TestCaseRepositoryFilters(): JSX.Element {
  const {
    isFilterApplied,
    projectFilter,
    statusesFilter,
    textSearchFilter,
    testTypeFilter,
    setProjectFilter,
    setTextSearchFilter,
    setStatusesFilter,
    setTestTypeFilter,
  } = useTestCaseRepositoryFiltersContext();

  const { loading, searchIssues } = useTestCaseRepositoryIssuesContext();
  const { testTypes } = useTestTypesContext();

  const handleFilterClick = async () => {
    await searchIssues();
  };

  return (
    <ContainerStyled>
      <SelectWrapperStyled>
        <RecentAndMoreProjectSelect
          onChange={setProjectFilter}
          isMulti
          isClearable
          useAllOption
          addNameOfFilter={false}
          isDisabled={!FEATURE_FLAG.isPremium}
          value={projectFilter.length > 0 ? projectFilter : ALL_PROJECTS}
          tooltip={!FEATURE_FLAG.isPremium && PREMIUM_FEATURE_TOOLTIP_MESSAGE}
        />
      </SelectWrapperStyled>

      {StorageOfUniqueServices.get('LicenseService').isAdvanced && (
        <SelectWrapperStyled>
          <TestTypeMultiSelect
            testTypes={testTypes}
            isTestTypesLoaded={!!testTypes}
            onChange={setTestTypeFilter}
            selectedTestTypes={testTypeFilter}
            tooltipContent={
              !FEATURE_FLAG.isPremium && PREMIUM_FEATURE_TOOLTIP_MESSAGE
            }
          />
        </SelectWrapperStyled>
      )}

      <SelectWrapperStyled>
        <IssueStatusProvider>
          <IssueStatusesContext.Consumer>
            {({ issueStatuses, isIssueStatusesLoaded }) => (
              <IssueStatusPicker
                issueStatuses={issueStatuses}
                isIssueStatusesLoaded={isIssueStatusesLoaded}
                onChange={setStatusesFilter}
                selectedIssueStatuses={statusesFilter}
                tooltipContent={
                  !FEATURE_FLAG.isPremium && PREMIUM_FEATURE_TOOLTIP_MESSAGE
                }
              />
            )}
          </IssueStatusesContext.Consumer>
        </IssueStatusProvider>
      </SelectWrapperStyled>

      <TextSearchContainerStyled>
        <TextSearch
          value={textSearchFilter}
          tooltipContent={
            !FEATURE_FLAG.isPremium && PREMIUM_FEATURE_TOOLTIP_MESSAGE
          }
          onChange={setTextSearchFilter}
          isDisabled={!FEATURE_FLAG.isPremium}
        />
      </TextSearchContainerStyled>

      <LoadingButtonWithTooltip
        loadingButtonProps={{
          onClick: handleFilterClick,
          appearance: isFilterApplied ? 'default' : 'primary',
          style: { height: '100%', width: '60px', padding: '4px' },
          isDisabled: loading !== 'none' || !FEATURE_FLAG.isPremium,
          isLoading: loading === 'search' || loading === 'search-more',
        }}
        tooltipProps={{
          content: !FEATURE_FLAG.isPremium && PREMIUM_FEATURE_TOOLTIP_MESSAGE,
        }}
      >
        Filter
      </LoadingButtonWithTooltip>
    </ContainerStyled>
  );
}
